/** Resume marketplace onboarding after Clerk OAuth (Google) full-page redirect. */
const CLERK_AUTH_APP_ORIGIN = "https://app.easygpt.builders";

export const PENDING_MARKETPLACE_ONBOARDING_REDIRECT_KEY =
  "egpt-pending-marketplace-onboarding-redirect";

export const PENDING_MARKETPLACE_ONBOARDING_REDIRECT_COOKIE =
  "egpt_pending_hire_redirect";

const PENDING_REDIRECT_MAX_AGE_SEC = 60 * 30;

function sharedCookieDomain(hostname: string): string | undefined {
  if (hostname === "localhost" || hostname.startsWith("127.")) return undefined;
  if (hostname.endsWith("easygpt.builders")) return ".easygpt.builders";
  if (hostname.endsWith("practiceclaw.com")) return ".practiceclaw.com";
  return undefined;
}

function writePendingRedirectCookie(url: string): void {
  if (typeof document === "undefined") return;
  const domain = sharedCookieDomain(window.location.hostname);
  const encoded = encodeURIComponent(url);
  let cookie = `${PENDING_MARKETPLACE_ONBOARDING_REDIRECT_COOKIE}=${encoded}; path=/; max-age=${PENDING_REDIRECT_MAX_AGE_SEC}; SameSite=Lax`;
  if (domain) cookie += `; domain=${domain}`;
  document.cookie = cookie;
}

function safeDecodeRedirectCookie(raw: string): string {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function readPendingRedirectCookie(): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${PENDING_MARKETPLACE_ONBOARDING_REDIRECT_COOKIE}=`;
  for (const part of document.cookie.split(";")) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return safeDecodeRedirectCookie(trimmed.slice(prefix.length)).trim() || null;
    }
  }
  return null;
}

function clearPendingRedirectCookie(): void {
  if (typeof document === "undefined") return;
  const domain = sharedCookieDomain(window.location.hostname);
  let cookie = `${PENDING_MARKETPLACE_ONBOARDING_REDIRECT_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
  if (domain) cookie += `; domain=${domain}`;
  document.cookie = cookie;
}

export function writePendingMarketplaceOnboardingRedirect(url: string): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(PENDING_MARKETPLACE_ONBOARDING_REDIRECT_KEY, url);
    writePendingRedirectCookie(url);
  } catch {
    /* ignore */
  }
}

export function readPendingMarketplaceOnboardingRedirect(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return (
      readPendingRedirectCookie() ??
      sessionStorage.getItem(PENDING_MARKETPLACE_ONBOARDING_REDIRECT_KEY)?.trim() ??
      null
    );
  } catch {
    return null;
  }
}

export function clearPendingMarketplaceOnboardingRedirect(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(PENDING_MARKETPLACE_ONBOARDING_REDIRECT_KEY);
    clearPendingRedirectCookie();
  } catch {
    /* ignore */
  }
}

function resolveAppOriginForRedirect(): string {
  const fromEnv = process.env.NEXT_PUBLIC_APP_URL?.trim().replace(/\/$/, "");
  if (fromEnv) return fromEnv;

  if (typeof window !== "undefined") {
    const { hostname } = window.location;
    if (hostname === "www.easygpt.builders" || hostname === "easygpt.builders") {
      return CLERK_AUTH_APP_ORIGIN;
    }
    return window.location.origin;
  }

  return CLERK_AUTH_APP_ORIGIN;
}

/** Clerk OAuth / email verification requires an absolute URL on the app host. */
export function toAbsoluteAuthRedirectUrl(pathOrUrl: string): string {
  if (pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://")) {
    return pathOrUrl;
  }
  return new URL(pathOrUrl, resolveAppOriginForRedirect()).href;
}

export function marketplaceRedirectPath(url: string): string {
  try {
    const parsed = url.startsWith("http") ? new URL(url) : new URL(url, "http://local");
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
}

export function isSameMarketplaceRedirect(currentPathAndSearch: string, target: string): boolean {
  return marketplaceRedirectPath(currentPathAndSearch) === marketplaceRedirectPath(target);
}

/** Resolve a stored redirect to a hire onboarding path for middleware redirects. */
export function resolvePendingMarketplaceRedirectPath(stored: string): string | null {
  try {
    const decoded = safeDecodeRedirectCookie(stored.trim());
    const path = marketplaceRedirectPath(decoded);
    if (path.startsWith("/agents/hire")) return path;
    if (path.startsWith("/agents")) return path;
    return null;
  } catch {
    return null;
  }
}

export function hasClerkPostAuthSearchParams(
  searchParams: URLSearchParams | string,
): boolean {
  const params =
    typeof searchParams === "string" ? new URLSearchParams(searchParams) : searchParams;
  return (
    params.has("__clerk_status") ||
    params.has("__clerk_created_session") ||
    params.has("__clerk_ticket")
  );
}

export function clearPendingMarketplaceRedirectCookieHeader(
  hostname: string,
): { name: string; value: string; path: string; maxAge: number; domain?: string } {
  const domain = sharedCookieDomain(hostname);
  return {
    name: PENDING_MARKETPLACE_ONBOARDING_REDIRECT_COOKIE,
    value: "",
    path: "/",
    maxAge: 0,
    ...(domain ? { domain } : {}),
  };
}
